import { useCallback } from 'react'
import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import type { MyClassesResponse } from './types'

const EMPTY: MyClassesResponse = { eligible: false, people: [], classes: [] }

export function myClassesCacheKey(gymId: string): string {
  return `my-classes:${gymId}`
}

/**
 * The classes this account can book at a gym, for itself and its children.
 *
 * The nav asks too, to decide whether Classes is offered at all, and shares
 * this request through the cache.
 */
export function useMyClasses(gymId: string | null) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const key = isAuthenticated && gymId ? myClassesCacheKey(gymId) : null

  const { data, loading, error, refresh } = useCachedFetch<MyClassesResponse>(key, async () => {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/my-classes?gymId=${encodeURIComponent(gymId ?? '')}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Failed to load classes')
    return (await res.json()) as MyClassesResponse
  })

  /** Book or give back a place. Resolves to the error code, or null on success. */
  const setBooked = useCallback(
    async (sessionId: string, childId: string | null, book: boolean): Promise<string | null> => {
      const token = await getAccessTokenSilently()
      const query = !book && childId ? `?childId=${childId}` : ''
      const res = await fetch(`${apiUrl}/my-classes/${sessionId}/book${query}`, {
        method: book ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: book ? JSON.stringify({ childId }) : undefined,
      })
      // Whatever happened, the places on screen may be stale now.
      await refresh()
      if (res.ok) return null
      const body = (await res.json().catch(() => ({}))) as { code?: string; reason?: string }
      // Out of entries is its own message: the plan is fine, it is just used up.
      if (body.reason === 'no_entries') return 'NO_ENTRIES'
      return body.code ?? 'ERROR'
    },
    [apiUrl, getAccessTokenSilently, refresh],
  )

  const value = data ?? EMPTY
  return { ...value, loading, error, setBooked }
}
